import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { actualizarDatos, getDataList } from '../../../api/common';

/**
 * ModalEditarEquipo - Modal para editar equipos agregados a la OT
 * @param {Object} props - Component props
 * @param {boolean} props.show - Control de visibilidad del modal
 * @param {Object} props.equipo - Equipo a editar
 * @param {Array} props.listaSedes - Lista de sedes del cliente
 * @param {Function} props.onGuardar - Handler que se ejecuta cuando se guarda (recibe equipoId, datosActualizados)
 * @param {Function} props.onCancelar - Handler para cerrar modal
 * @returns {JSX.Element}
 */
const ModalEditarEquipo = ({ show, equipo, listaSedes = [], onGuardar, onCancelar }) => {
    const [guardando, setGuardando] = useState(false);
    const [items, setItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [formData, setFormData] = useState({
        item: '',
        sedeClient: '',
        marca: '',
        modelo: '',
        serie: '',
        inventario: '',
        ubicacion: '',
        resolucion: '',
        puntos: '',
        observacion: ''
    });

    // Cargar items al montar
    useEffect(() => {
        if (show) {
            loadItems();
        }
    }, [show]);

    // Inicializar formData cuando cambia el equipo
    useEffect(() => {
        if (equipo) {
            setFormData({
                item: equipo.item?._id || '',
                sedeClient: equipo.sedeClient?._id || '',
                marca: equipo.marca || '',
                modelo: equipo.modelo || '',
                serie: equipo.serie || '',
                inventario: equipo.inventario || '',
                ubicacion: equipo.ubicacion || '',
                resolucion: equipo.resolucion || '',
                puntos: equipo.puntos || '',
                observacion: equipo.observacion || ''
            });
        }
    }, [equipo]);

    const loadItems = async () => {
        setLoadingItems(true);
        try {
            const response = await getDataList('Items');
            setItems(response.data.data || []);
        } catch (error) {
            console.error('Error cargando items:', error);
            Swal.fire('Error', 'No se pudieron cargar los items', 'error');
        } finally {
            setLoadingItems(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleGuardar = async (e) => {
        e.preventDefault();
        
        console.log('💾 ModalEditarEquipo: Guardando equipo', equipo._id);
        console.log('💾 Datos a guardar:', formData);

        // Validaciones
        if (!formData.item) {
            Swal.fire('Error', 'El item es requerido', 'error');
            return;
        }
        if (!formData.sedeClient) {
            Swal.fire('Error', 'La sede es requerida', 'error');
            return;
        }

        setGuardando(true);

        try {
            // Actualizar equipo en BD
            const responseUpdate = await actualizarDatos('Equipos', formData, equipo._id);
            // Nuevo shape (spec equipos-dependientes-sincronizacion-cancelacion):
            //   { data, propagados: [<equipos actualizados por sincronización>] }
            const propagadosCount = responseUpdate?.data?.propagados?.length || 0;

            // Buscar la sede completa y el item completo
            const sedeCompleta = listaSedes.find(s => s._id === formData.sedeClient);
            const itemCompleto = items.find(i => i._id === formData.item);

            console.log('🏢 Sede encontrada:', sedeCompleta);
            console.log('📦 Item encontrado:', itemCompleto);

            // Preparar datos actualizados con referencias pobladas
            const equipoActualizado = {
                ...equipo,  // Mantener todos los campos originales
                ...formData,  // Sobrescribir con ediciones
                sedeClient: sedeCompleta || equipo.sedeClient,
                item: itemCompleto || equipo.item
            };
            
            console.log('📤 Llamando onGuardar con:', equipoActualizado);

            // Notificar al componente padre
            onGuardar(equipo._id, equipoActualizado);

            Swal.fire({
                icon: 'success',
                title: 'Equipo actualizado',
                text: propagadosCount > 0
                    ? `Los cambios se guardaron correctamente. Se sincronizaron ${propagadosCount} equipo(s) dependiente(s).`
                    : 'Los cambios se guardaron correctamente',
                timer: 1500,
                showConfirmButton: false
            });

            onCancelar();

        } catch (error) {
            console.error('Error actualizando equipo:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo actualizar el equipo', 'error');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal show={show} onHide={onCancelar} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Editar Equipo</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Form onSubmit={handleGuardar}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Item <span className="text-danger">*</span></Form.Label>
                                {loadingItems ? (
                                    <div className="text-center">
                                        <Spinner animation="border" size="sm" /> Cargando items...
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="item"
                                        value={formData.item || ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Seleccione un item...</option>
                                        {items.map(item => (
                                            <option key={item._id} value={item._id}>
                                                {item.nombre}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sede <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="sedeClient"
                                    value={formData.sedeClient || ''}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Seleccione una sede...</option>
                                    {listaSedes.map(sede => (
                                        <option key={sede._id} value={sede._id}>
                                            {sede.nameSede}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Marca <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="marca"
                                    value={formData.marca || ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Modelo <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="modelo"
                                    value={formData.modelo || ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Serie <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="serie"
                                    value={formData.serie || ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Inventario</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="inventario"
                                    value={formData.inventario || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ubicación</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="ubicacion"
                                    value={formData.ubicacion || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Resolución</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="resolucion"
                                    value={formData.resolucion || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Puntos a Calibrar</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="puntos"
                                    value={formData.puntos || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Observación</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="observacion"
                                    value={formData.observacion || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancelar} disabled={guardando}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleGuardar} disabled={guardando || loadingItems}>
                    {guardando ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Guardando...
                        </>
                    ) : (
                        'Guardar Cambios'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalEditarEquipo;
